import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { injectInfiniteQuery, injectMutation, injectQuery, injectQueryClient, Query, QueryObserverResult } from '@ngneat/query'
import { catchError, firstValueFrom, forkJoin, map, mergeMap, Observable, of, tap } from 'rxjs'
import type { CitationMinuteNodes } from '../../../backend/src/citations/minutes-citations-api.dto'
import { DemoProfilesService } from './demo-profiles.service'
import { DownloadsService } from './downloads.service'
import { CallFilters } from './filters/filters.component'
import { GuestOrganization } from './guest-organizations.service'
import { ListParameterQueryOptional } from './list-query-options'
import { LoggerService } from './logger.service'
import { MetricsService } from './metrics.service'
import { Organization } from './organizations.service'
import { queryKeys } from './query-keys'
import { MINIMUM_SEGMENT_DURATION } from './speaker-x-ray/speaker-x-ray.component'
import { ToastService } from './toast.service'
import { User } from './users.service'
import { MinuteTimelineSegment, TemplateContentBlockSourceType, TemplateContentBlockType } from './template-editor.component/types/template.types'
import { DocumentUploadInfo, generateFileName, generateHeadersWithTimezone } from './utils'
import { CallProcessingStatus, CallSessionStatus, CallStatus, CallType } from './models/call-enums'
import type { BatchJobType, CallProvider } from './models/call-enums'

export { CallProcessingStatus, CallSessionStatus, CallStatus, CallType } from './models/call-enums'
export type { BatchJobType, CallProvider } from './models/call-enums'

export function anyFiltersApplied(filters: CallFilters, includeQuery = true): boolean {
  const isQueryActive = includeQuery ? filters.query !== null && filters.query !== '' : false
  return (
    isQueryActive ||
    filters.guestOrganizationId !== null ||
    filters.attendeeId !== null ||
    filters.speakerIds.length > 0 ||
    filters.startDate !== null ||
    filters.endDate !== null
  )
}

export type ExtendResult = { status: 'EXTENDED' | 'NOT_EXTENDED' | 'ERROR'; leaveDate: string | null }
export type ExtendPreview = { status: 'EXTENDABLE' | 'NOT_EXTENDABLE' | 'ERROR'; leaveDate: string | null }

export type PastCallsType = 'ALL' | 'OWN_CALLS' | 'SHARED_WITH_ME'
export type CallListType = 'upcoming' | 'past' | 'upcoming-search' | 'past-search'
export type AgendaType = 'SINGLE' | 'MULTIPLE'
export type Sentence = {
  sentenceId: string
  content: string
  startTimeSeconds: number
  endTimeSeconds: number
}

export type TranscriptSpeakerSlot = {
  type: 'speaker'
  speaker: Speaker
  startTime: number
  sentences: Sentence[]
  duration: number
}

export type TranscriptPauseSlot = {
  type: 'pause'
  startTime: number
  endTime: number
  actualDuration: number
}

export type TranscriptSlot = TranscriptSpeakerSlot | TranscriptPauseSlot

export type Transcript = TranscriptSlot[]

export type ActionItem = {
  id: number
  item: string
  assignee: string | null
  deadline: string | null
}

export type Challenge = {
  id: number
  item: string
  reason: string | null
  person: string | null
}

export type OutlineItem = {
  id: number
  title: string
  startTimeSeconds: number
  startSentenceId: string
  endSentenceId: string
  discussion: string
  actionItems: ActionItem[]
  challenges: Challenge[]
  decisions: string[]
}

export type SearchMatch = {
  sentence: string
  sentenceSearchId: string
  speakerId: string
  speakerName: string | null
  startTimeSeconds: number
}

export type CallAccessLevel = 'VIEWER' | 'COMMENTER' | 'EDITOR' | 'FULL_ACCESS'
export type CallAttendanceType = 'INTERNAL' | 'EXTERNAL'

export type CallPublicLinkAvailability = 'PARTICIPANTS_AND_PEOPLE_WITH_ACCESS_ONLY' | 'SAME_ORGANIZATION'

export type CallShare = {
  sharedUser: User
  sharedUserId: number
  level: CallAccessLevel
}

export type Speaker = {
  speakerId: string
  name: string | null
  speakerTag: string
  startTimeSeconds: number
  originalTag: string | null
}

export type MeetingRoomScore = {
  roomName: string
  averageConfidenceScore: number | null
  averageRms: number
  averageHfRatio: number
  totalSpeakingTimeSeconds: number
  physicalSpeakerTags: string[]
}

export type RecentInteraction = {
  id: number
  date: Date
  provider: CallProvider
  callName: string
}

export type MeetingInviteStatus = 'ACCEPT' | 'TENTATIVE' | 'DECLINE'

export type GroupAttendee = {
  email: string
  name: string
  response: MeetingInviteStatus
  isOrganizer: boolean
}

export type GroupedAttendeeOrganization = {
  organizationName: string
  attendees: GroupAttendee[]
}

export type CallResourceType =
  | 'USER_UPLOADED_VIDEO'
  | 'USER_UPLOADED_AUDIO'
  | 'BOT_RECORDED_VIDEO'
  | 'BOT_RECORDED_AUDIO'
  | 'DISPLAY_VIDEO'
  | 'DISPLAY_AUDIO'
  | 'PROCESSED_AUDIO'
  | 'RESIZED_THUMBNAIL'
  | 'ORIGINAL_THUMBNAIL'
  | 'HALO_SPEAKERS'
  | 'AGENDA'
  | 'CUSTOM_DOCUMENT'
  | 'BOARD_PACK'

export type CallResource = {
  id: number
  createdAt: string
  updatedAt: string
  callId: number
  resourceType: CallResourceType
  fileName: string
  extension: string
  cloudStorageFileName: string
  index: number
  deletedAt: string | null
  downloadUrl: string | null
  deletionReason: string | null
  uploadedByUser: boolean
  uploadedByIngestBoardPackJob: boolean
  size: number
  metadata: CallResourceMetadata | null
}

export type CallResourceWithUrl = CallResource & {
  downloadUrl: string | null
}

export type CallResourceMetadata = {
  numberOfPages: number
  tokenCount: number | null
}

export type AdminCallResourceMetadata = {
  numberOfPages: number
  tokenCount: number | null
  hasDescription: boolean
  keywordCount: number
}

export type AdminCallResource = Omit<CallResource, 'metadata' | 'fileName'> & {
  fileName: string | null
  metadata: AdminCallResourceMetadata | null
}

export type AgendaNodeBase = {
  id: number
  title: string
  presenters: string
  category: AgendaItemCategory
  subCategories: AgendaItemSubCategory[]
  itemNumber: string | null
  subitems: AgendaNode[]
  parentId: number | null
}

export type AgendaLeafNode = AgendaNodeBase & {
  category: string
  subCategories: string[]
}

export type AgendaBranchNode = AgendaNodeBase & {}

export type AgendaNode = AgendaBranchNode | AgendaLeafNode
export type Agenda = AgendaNode[]

export type RawAgendaItem = {
  id: number
  title: string
  presenters: string | null
  category: AgendaItemCategory
  subCategories: AgendaItemSubCategory[]
  parentId: number | null
  itemNumber: string | null
  orderNumber: number
}

export type MoveAgendaNodeData = {
  nodeId: number
  index: number
}

export type PauseInterval = {
  start: number
  end: number
  actualDuration: number
}

export type SpeakerMapping = { [key: string]: string }
export type MinutesGenerationTemplateProgress = { level: string; index: number; total: number }
export type Call = {
  transcript: Transcript
  id: number
  name: string
  allowTranscriptDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  organizationId: number
  organizationName: string
  provider: string
  description: string | null
  status: CallStatus
  endReason: string | null
  errorReason: string | null
  context: string[]
  date: Date
  endDate: Date
  actualStartDate: Date
  actualEndDate: Date
  queuedAt: Date
  waitingToBeAdmittedAt: Date
  createdAt: Date
  duration: number | null
  thumbnailUrl: string | null
  botInstructedToJoinByOtherUsers: boolean
  videoUrl: string | null
  audioUrl: string | null
  isPrevious: boolean
  isOngoing: boolean
  isUpcoming: boolean
  isMinutesGenerationOngoing: boolean
  minutesGenerationProgress: number | null
  minutesGenerationTemplateProgress: MinutesGenerationTemplateProgress | null
  editorContent: string
  hasMinutes: boolean
  hasErrorEmailBeenSent: boolean
  hasSoftDeletionWarningEmailBeenSent: boolean
  hasSoftDeletionConfirmationEmailBeenSent: boolean
  hasUpcomingCallReminderEmailBeenSent: boolean
  transcriptTokenCount: number | null
  matches?: SearchMatch[] // TODO Lazy typing as the types for search and normal call showing are different...
  publicLinkAvailability: CallPublicLinkAvailability
  publicLinkAccessLevel: CallAccessLevel
  publicLinkSecret: string
  accessLevel: CallAccessLevel
  attendanceType: CallAttendanceType
  isSetToJoin: boolean
  notifyWhenPostProcessingCompletes: boolean
  userSpeakerMapping: SpeakerMapping | null
  automatedSpeakerMapping: SpeakerMapping | null
  processingStatus: CallProcessingStatus | null
  processingProgress: number | null
  hangUpKey: string | null
  haveSpeakersBeenUpdated: boolean
  isMinutingUpToDate: boolean
  isOutlineUpToDate: boolean
  counterpartyNames: string[]
  currentParticipantCount: number | null
  link: string | null
  isSpeechMissing: boolean
  isPostProcessingComplete: boolean
  isTranscriptionComplete: boolean
  allowMediaDownload: boolean
  markedToBeDeletedAt: Date | null
  calendarSyncLostAt: Date | null
  isTranscriptReady: boolean
  isMediaReady: boolean
  isOutlineReady: boolean
  physicalSpeakersExist: boolean
  type: CallType
  medium: CallMedium
  attendeeNames: string[]
  hasVirtualSpeakers: boolean
  participantCount: number
  callProfileId: number | null
  hasWarningForMissingAgendaBeenShown: boolean
  haveMinuteStylesBeenUpdatedSinceLastProcessing: boolean
  totalNumberOfSplits: number
  templateId: number | null
  callSetIndex: number | null
  callSetSize: number | null
  finalDeletionDate: Date | null
  retentionPeriodExpirationDate: Date | null
  retentionPeriodInDaysForCall: number | null
  retentionPeriodStartDate: Date | null
  avgConfidenceScore: number | null

  outlineItems: OutlineItem[]
  shares: CallShare[]
  users: User[]
  speakers: Speaker[]
  recentInteractions: RecentInteraction[]
  groupedAttendees: GroupedAttendeeOrganization[]

  agendaDownloadUrl: string | null
  userUploadedResources: CallResourceWithUrl[]
  uploadedRecording: CallResource | null
  agenda: Agenda
  agendaType: AgendaType
  templateSourceType: TemplateSourceType | null
  pauseIntervals: PauseInterval[]
  useNewEditor: boolean

  minutesCitations: CitationMinuteNodes | null
  agendaMetadata: AdminAgendaMetadata
  agendaExtractionProgress: number | null
  agendaExtractionFailed: boolean
  invalidAgendaReason: InvalidAgendaReason | null
  isCheckingAgendaValidity: boolean
  hasTemplateItems: boolean
  isIngestingBoardPack: boolean
  boardPackIngestionProgress: number | null
  boardPackIngestionFailed: boolean
  templateApprovedByUser: boolean
  isParticipantExtractionInProgress: boolean
  lastSpeechEndTime: number | null
  transcriptLanguage: string | null
  allowSystemGuidanceEditing: boolean
} & MinutesStyling

export type CallWithLatestSession = Call & {
  latestCallSession: CallSession | null
}

export type CallTimelineEvent = {
  type: string
  timestamp: Date
  userName: string | null
  metadata: any | null
}

export type CallTimelineUserActivity = {
  userName: string
  hasEnabledAssistant: boolean
  viewCount: number
  lastViewedAt: Date | null
  lastEnabledAt: Date | null
  lastDisabledAt: Date | null
}

export type CallTimelineResponse = {
  events: CallTimelineEvent[]
  userActivities: CallTimelineUserActivity[]
}

export type AdminOutlineStructureItem = {
  id: number
  actionItemCount: number
  challengeCount: number
  decisionCount: number
  startTimeSeconds: number
  hasError: boolean
}

export type AdminOutlineStructure = {
  items: AdminOutlineStructureItem[]
}

export enum AgendaAttendeeType {
  CHAIR = 'CHAIR',
  MEMBER = 'MEMBER',
  STANDING_ATTENDEE = 'STANDING_ATTENDEE',
  ATTENDEE_BY_INVITATION = 'ATTENDEE_BY_INVITATION',
  OBSERVER = 'OBSERVER',
  COMPANY_SECRETARY = 'COMPANY_SECRETARY',
  APOLOGY = 'APOLOGY',
  OTHER = 'OTHER',
}

export enum GoverningBodyType {
  BOARD = 'BOARD',
  COMMITTEE = 'COMMITTEE',
  OTHER = 'OTHER',
}

export enum TemplateSourceType {
  UPLOAD = 'UPLOAD',
  CREATE = 'CREATE',
  LOAD = 'LOAD',
}

export function computeIsCheckingAgendaValidity(call: Pick<Call, 'isCheckingAgendaValidity'>): boolean {
  return call.isCheckingAgendaValidity
}

export function isAgendaExtractionRejectionVisible(call: Pick<Call, 'invalidAgendaReason' | 'agendaExtractionFailed' | 'templateSourceType'>): boolean {
  return (call.invalidAgendaReason !== null || call.agendaExtractionFailed) && call.templateSourceType === TemplateSourceType.UPLOAD
}

export type AgendaUploadStatus = 'idle' | 'uploading' | 'checking' | 'failed' | 'uploaded'

export function computeAgendaUploadStatus(params: { isImporting: boolean; isFailed: boolean; isChecking: boolean; isUploaded: boolean }): AgendaUploadStatus {
  if (params.isImporting) {
    return 'uploading'
  }
  if (params.isFailed) {
    return 'failed'
  }
  if (params.isChecking) {
    return 'checking'
  }
  if (params.isUploaded) {
    return 'uploaded'
  }
  return 'idle'
}

export function computeAgendaFailureMessageKey(invalidAgendaReason: InvalidAgendaReason | null, agendaExtractionFailed: boolean): string | null {
  if (invalidAgendaReason !== null) {
    return `CALL_SETUP.TEMPLATE_REVIEW.INVALID_AGENDA_REASONS.${invalidAgendaReason}`
  }
  if (agendaExtractionFailed) {
    return 'CALL_SETUP.TEMPLATE_REVIEW.EXTRACTION_FAILED'
  }
  return null
}

export function computeHasUsableTemplateSource(params: {
  hasAgendaUploaded: boolean
  templateSourceType: TemplateSourceType | null
  isCheckingAgendaValidity: boolean
  isImportingAgenda: boolean
  isAgendaExtractionFailed: boolean
}): boolean {
  const hasTemplateSource = params.hasAgendaUploaded || params.templateSourceType !== null
  if (!hasTemplateSource) {
    return false
  }
  return !params.isCheckingAgendaValidity && !params.isImportingAgenda && !params.isAgendaExtractionFailed
}

export function computeShouldConfirmTemplateReplace(params: {
  templateSourceType: TemplateSourceType | null
  isCheckingAgendaValidity: boolean
  isImportingAgenda: boolean
  isAgendaExtractionFailed: boolean
  isCreateTemplateSelected: boolean
  templateItemsCount: number
}): boolean {
  if (params.templateSourceType === null) {
    return false
  }
  if (params.isCheckingAgendaValidity || params.isImportingAgenda || params.isAgendaExtractionFailed) {
    return false
  }
  if (params.isCreateTemplateSelected && params.templateItemsCount === 0) {
    return false
  }
  return true
}

export function computeShouldShowTemplateSourcePicker(params: {
  isCheckingAgendaValidity: boolean
  isAgendaExtractionFailed: boolean
  hasTemplateItems: boolean
  itemCount: number
  templateSourceType: TemplateSourceType | null
}): boolean {
  if (params.isCheckingAgendaValidity) {
    return true
  }
  if (params.isAgendaExtractionFailed && !params.hasTemplateItems) {
    return true
  }
  return params.itemCount === 0 && params.templateSourceType === null
}

export function computeCurrentStep(params: { isUploadedCall: boolean; isMultipleAgenda: boolean; hasUsableTemplateSource: boolean }): number {
  if (params.isUploadedCall) {
    return params.hasUsableTemplateSource ? 4 : 2
  }
  if (params.isMultipleAgenda) {
    return 3
  }
  return params.hasUsableTemplateSource ? 4 : 3
}

export function computeIsTemplateReviewVisible(params: {
  isMultipleAgenda: boolean
  currentStep: number
  reviewStepNumber: number
  isPopulatingTemplate: boolean
  isIngestingBoardPack: boolean
  hasCall: boolean
  hasMetadata: boolean
}): boolean {
  return (
    !params.isMultipleAgenda &&
    (params.currentStep >= params.reviewStepNumber || params.isPopulatingTemplate || params.isIngestingBoardPack) &&
    params.hasCall &&
    params.hasMetadata
  )
}

export type AdminAgendaAttendee = {
  id: number
  firstName: string
  lastName: string
  role: string | null
  entity: string | null
  type: AgendaAttendeeType
  initials: string | null
  honorifics: string | null
}

export type AdminAgendaMetadata = {
  id: number
  companyName: string | null
  location: string | null
  date: string | null
  time: string | null
  entities: string[]
  committeeType: string | null
  governingBodyType: GoverningBodyType | null
  attendees: AdminAgendaAttendee[]
}

export type CallUser = {
  user: User
  isSetToJoin: boolean
  isSetToJoinFrozenByUser: boolean
  notifyWhenPostProcessingCompletes: boolean
  hasWeekendDeletionReminderEmailBeenSent: boolean
  isPrimaryCalendar: boolean
}

export type CallSession = {
  index: number
  actualStartDate: Date | null
  actualEndDate: Date | null
  status: CallSessionStatus
  duration: number | null
  errorReason: string | null
  endReason: string | null
  botErrorReason: string | null
  waitingToBeAdmittedAt: Date | null
  lastHeartbeatReceivedAt: Date | null
  pauseIntervals: PauseInterval[]
  originalSplitOffsets: number[]
  adjustedSplitOffsets: number[]
  cloudRunExecutionName: string | null
  invoker: string | null
  useNativeBot: boolean
}

export type CallSessionWithSegments = CallSession & {
  segments: CallSegment[]
}

export type CallSegment = {
  callId: number
  sessionIndex: number
  index: number
  startOffset: number
  duration: number
}

export type AnonymizedCallParticipation = {
  joinedAt: Date
  leftAt: Date | null
}

export type AnonymizedCallParticipant = {
  id: number
  consented: boolean | null
  consentRequestedAt: Date | null
  consentRespondedAt: Date | null
  participations: AnonymizedCallParticipation[]
}

export type InvalidAgendaReason = 'NO_READABLE_TEXT' | 'NO_AGENDA_ITEMS' | 'BOARD_PACK' | 'MINUTES_NOT_AGENDA' | 'OTHER'

export type CallBatchJobRun = {
  id: number
  type: BatchJobType
  task: string | null
  registeredAt: Date | null
  responseAt: Date | null
  successful: boolean | null
  error: string | null
  metadata: any
  callId: number
  completionPercentage: number
  executionGroupId: string
  triggeredBy: string
}

export type CallBatchJobRunWithDuration = CallBatchJobRun & {
  duration: number
  agendaTranscriptMapping?: AgendaTranscriptMappingResponse | null
}

export type CallBatchJobDetails = {
  singleRuns: CallBatchJobRunWithDuration[]
}

export type InProgressBatchJob = CallBatchJobRunWithDuration & {
  organizationName: string
}

export type UploadedDocumentInfo = {
  resourceType: 'AGENDA' | 'CUSTOM_DOCUMENT' | 'BOARD_PACK'
  fileName: string
  size: number
}

export type DebugDataFile = {
  key: string
  fileName: string
  url: string
  lastModified: string
  mediaType: 'VIDEO' | 'IMAGE' | 'OTHER'
}

export type EditorDebugRevision = {
  id: number
  userId: number | null
  hasContent: boolean
  hasContentRaw: boolean
  createdAt: string
  updatedAt: string
}

export type EditorDebugLoadingInputs = {
  isPostProcessingComplete: boolean
  isMinutesGenerationOngoing: boolean
  minutesGenerationProgress: number | null
}

export type EditorDebugSyncReadiness = {
  hasPersistedYDocument: boolean
  hasAnyRevision: boolean
}

export type EditorAdminDebug = {
  revisions: EditorDebugRevision[]
  loadingInputs: EditorDebugLoadingInputs
  syncReadiness: EditorDebugSyncReadiness
}

export type HallucinationAuditSegment = {
  start: number
  end: number
  avg_logprob: number | null
  word_count: number
  word_score_min: number | null
  word_score_mean: number | null
  text: string | null
  sentenceId: string | null
}

export type HallucinationAuditData = {
  callId: number
  jobId: number
  artifactMissing: boolean
  segments: HallucinationAuditSegment[]
}

export type AgendaTranscriptMappingSegment = {
  startSeconds: number
  endSeconds: number
  confidence: 'HIGH' | 'LOW'
}

export type AgendaTranscriptMappingItem = {
  agendaItemId: number | null
  itemNumber: string
  depth: number
  topLevelAncestorId: number | null
  hasChildren: boolean
  contentBlockTypes: string[]
  orderIndex: number
  status: 'DISCUSSED' | 'NOT_DISCUSSED' | 'NO_DISCUSSION_FOUND'
  segments: AgendaTranscriptMappingSegment[]
}

export type AgendaTranscriptMappingUnmappedRegion = {
  startSeconds: number
  endSeconds: number
  kind: 'MAPPING_GAP' | 'SILENCE'
}

export type AgendaTranscriptMappingResponse = {
  callId: number
  transcriptDurationSeconds: number
  agendaItems: AgendaTranscriptMappingItem[]
  unmappedRegions: AgendaTranscriptMappingUnmappedRegion[]
}

export type AgendaTranscriptMappingMode = 'DISABLED' | 'ENABLED' | 'ENABLED_HIGH_ONLY'

export type MinutingAgent = 'CONDENSATION' | 'COLLOQUIALISM' | 'NAMING'

export const MINUTING_AGENTS: MinutingAgent[] = ['CONDENSATION', 'COLLOQUIALISM', 'NAMING']

// Agents that always run and cannot be toggled off in the UI. Additional agents
// added later stay optional and remain selectable.
export const MANDATORY_MINUTING_AGENTS: MinutingAgent[] = ['CONDENSATION', 'COLLOQUIALISM', 'NAMING']

export type AnonymizedTemplateContentBlockSource = {
  type: TemplateContentBlockSourceType
  id: number | null
  fileName: string | null
}

export type AnonymizedBinaryOption = {
  placeholderCount: number
}

export type AnonymizedTemplateContentBlock = {
  type: TemplateContentBlockType
  levelOfDetail: MinutesVerboseness
  anonymization: MinutesAnonymization
  hasGuidance: boolean
  sources: AnonymizedTemplateContentBlockSource[]
  placeholderCount: number | null
  binaryOptions: AnonymizedBinaryOption[] | null
}

export type AnonymizedTemplateItem = {
  id: number
  index: number
  title: string | null
  presenterCount: number
  duration: number | null
  isSectionHeading: boolean
  hasSemanticSummary: boolean
  hasKeyVocabulary: boolean
  contentBlocks: AnonymizedTemplateContentBlock[]
  minuteTimelineSegments: MinuteTimelineSegment[]
  hasMinuteContent: boolean
  subitems: AnonymizedTemplateItem[]
}

export type AdminCallDetails = Pick<
  Call,
  | 'id'
  | 'status'
  | 'isSetToJoin'
  | 'date'
  | 'endDate'
  | 'actualStartDate'
  | 'actualEndDate'
  | 'createdAt'
  | 'speakers'
  | 'provider'
  | 'type'
  | 'isOngoing'
  | 'publicLinkAvailability'
  | 'publicLinkAccessLevel'
  | 'shares'
  | 'duration'
  | 'processingStatus'
  | 'userSpeakerMapping'
  | 'automatedSpeakerMapping'
  | 'haveSpeakersBeenUpdated'
  | 'markedToBeDeletedAt'
  | 'calendarSyncLostAt'
  | 'medium'
  | 'isSpeechMissing'
  | 'isMediaReady'
  | 'isTranscriptReady'
  | 'isOutlineReady'
  | 'isPostProcessingComplete'
  | 'endReason'
  | 'errorReason'
  | 'participantCount'
  | 'currentParticipantCount'
  | 'callProfileId'
  | 'agendaType'
  | 'templateId'
  | 'finalDeletionDate'
  | 'retentionPeriodExpirationDate'
  | 'retentionPeriodInDaysForCall'
  | 'retentionPeriodStartDate'
  | 'lastSpeechEndTime'
  | 'hasErrorEmailBeenSent'
  | 'hasSoftDeletionWarningEmailBeenSent'
  | 'hasSoftDeletionConfirmationEmailBeenSent'
  | 'hasUpcomingCallReminderEmailBeenSent'
> &
  MinutesStyling & {
    isOrganizedByThisOrganization: boolean
    attendeeCountsByGuestOrganization: { organizationName: string; attendeeCount: number; hasOrganizer: boolean }[]
    agendaAttendees: Pick<AdminAgendaAttendee, 'id' | 'type'>[]
    attendance: AnonymizedCallParticipant[]
    hasContext: boolean
    outlineStructure: AdminOutlineStructure
    callUsers: CallUser[]
    batchJobRuns: CallBatchJobDetails
    speakersFileDownloadUrl: string | null
    spectrogramUrl: string | null
    allResources: AdminCallResource[]
    organization: Organization
    templateSourceType: TemplateSourceType | null
    anonymizedTemplateItems: AnonymizedTemplateItem[] | null
    useNewEditor: boolean | null
    useNativeBot: boolean | null
    minutingAgents: MinutingAgent[]
    overrideOrganizationLevelMinutingAgents: boolean
    useVerificationDiscovery: boolean | null
    allowSystemGuidanceEditing: boolean
    useAgendaTranscriptMapping: AgendaTranscriptMappingMode | null
    anonymiseMemberSpeakers: boolean | null
    isMinutingUpToDate: boolean
    isOutlineUpToDate: boolean
    isAgendaTranscriptMappingUpToDate: boolean
    callSessions: CallSessionWithSegments[]
    latestCallSession: CallSession | null
    uploadedDocuments: UploadedDocumentInfo[]
    audioMetrics: {
      speakerDiagnostics: {
        segmentMetrics: {
          speaker: string
          startTimeSeconds: number
          endTimeSeconds: number
          durationSeconds: number
          rms: number
          peak: number
          hfRatio: number
          silenceRatio: number
          meanConfidenceScore?: number
          index: number
        }[]
        speakerMetrics: {
          [speaker: string]: {
            speaker: string
            totalSpeakingTimeSeconds: number
            segmentCount: number
            avgRms: number
            avgPeak: number
            avgHfRatio: number
            avgSilenceRatio: number
            avgConfidenceScore?: number
            haloSpeakerName?: string | null
          }
        }
        meetingMetrics: {
          totalDurationSeconds: number
          totalSegments: number
          totalSpeakers: number
          segmentsPerMinute: number
          avgRms: number
          avgPeak: number
          avgSilenceRatio: number
          avgHfRatio: number
          avgConfidenceScore?: number
        }
      } | null
    }
    avgConfidenceScore: number | null
    avgRms: number | null
    meetingRoomScores: MeetingRoomScore[]
    virtualSpeakers: Speaker[] | null
    virtualSpeakerMapping: SpeakerMapping | null
    keyName: string | null
  }

export type SearchResponse = Call[]

export type CallListResponse = {
  calls: Call[]
  hasMore: boolean
  nextCursor?: number
}

export enum PostProcessor {
  TRANSCODER = 'TRANSCODER',
  ASR = 'ASR',
  LLM_SPEAKER_LABELING = 'LLM_SPEAKER_LABELING',
  OUTLINE = 'OUTLINE',
  AGENDA_EXTRACTION = 'AGENDA_EXTRACTION',
  MINUTES = 'MINUTES',
  TRANSCODER_SPLIT = 'TRANSCODER_SPLIT',
  MAP_AGENDA_TO_TRANSCRIPT = 'MAP_AGENDA_TO_TRANSCRIPT',
  INGEST_BOARD_PACK = 'INGEST_BOARD_PACK',
}

export type LlmModel =
  | 'ANTHROPIC_CLAUDE_4_5_SONNET'
  | 'ANTHROPIC_CLAUDE_4_5_OPUS'
  | 'ANTHROPIC_CLAUDE_4_5_HAIKU'
  | 'ANTHROPIC_CLAUDE_4_6_OPUS'
  | 'ANTHROPIC_CLAUDE_4_6_SONNET'
  | 'ANTHROPIC_CLAUDE_4_7_OPUS'
  | 'GOOGLE_GEMINI_2_5_PRO'

export const LLM_MODELS: LlmModel[] = [
  'ANTHROPIC_CLAUDE_4_5_SONNET',
  'ANTHROPIC_CLAUDE_4_5_OPUS',
  'ANTHROPIC_CLAUDE_4_5_HAIKU',
  'ANTHROPIC_CLAUDE_4_6_OPUS',
  'ANTHROPIC_CLAUDE_4_6_SONNET',
  'ANTHROPIC_CLAUDE_4_7_OPUS',
  'GOOGLE_GEMINI_2_5_PRO',
]

type Attendee = {
  name: string
  email: string
}

export enum MinutesVerboseness {
  CONCISE = 'CONCISE',
  DETAILED = 'DETAILED',
  EXHAUSTIVE = 'EXHAUSTIVE',
}

export enum MinutesAnonymization {
  ALL = 'ALL',
  NONE = 'NONE',
}

export enum MinutesPassive {
  ACTIVE = 'ACTIVE',
  CONTEXTUAL = 'CONTEXTUAL',
  PASSIVE = 'PASSIVE',
}

export enum MinutesNameFormat {
  FIRST_NAME = 'FIRST_NAME',
  ROLE = 'ROLE',
  SURNAME = 'SURNAME',
  HONORIFIC_SURNAME = 'HONORIFIC_SURNAME',
  INITIALS = 'INITIALS',
  FULL_NAME = 'FULL_NAME',
  INITIAL_SURNAME = 'INITIAL_SURNAME',
}

export enum MinutesDateFormat {
  DD_MONTH_YYYY = 'DD_MONTH_YYYY',
  MONTH_DD_TH_YYYY = 'MONTH_DD_TH_YYYY',
  DD_MM_YYYY_DOT = 'DD_MM_YYYY_DOT',
  DD_MM_YYYY_SLASH = 'DD_MM_YYYY_SLASH',
}

export enum MinutesTimeFormat {
  TWENTY_FOUR_HOUR = 'TWENTY_FOUR_HOUR',
  TWELVE_HOUR = 'TWELVE_HOUR',
}

export enum MinutesLanguage {
  BRITISH = 'BRITISH',
  AMERICAN = 'AMERICAN',
  SPANISH = 'SPANISH',
  FRENCH = 'FRENCH',
}

export enum MinutesAttribution {
  OUTCOME = 'OUTCOME',
  COLLECTIVE = 'COLLECTIVE',
  CONTEXTUAL = 'CONTEXTUAL',
}

export enum CallMedium {
  PHYSICAL = 'PHYSICAL',
  VIRTUAL = 'VIRTUAL',
}

export enum MetricType {
  OPEN_APP = 'OPEN_APP',
  VIEW_CALL = 'VIEW_CALL',
  RUN_LLM_LIVE_ACTION = 'RUN_LLM_LIVE_ACTION',
  COMPLETE_LLM_LIVE_ACTION = 'COMPLETE_LLM_LIVE_ACTION',
  SAVE_EDITOR_CONTENT = 'SAVE_EDITOR_CONTENT',
  COMMENT_ON_EDITOR = 'COMMENT_ON_EDITOR',
  CONNECT_CALENDAR = 'CONNECT_CALENDAR',

  UPLOAD_MEETING = 'UPLOAD_MEETING',
  ADD_MANUAL_MEETING = 'ADD_MANUAL_MEETING',
  CLICK_ON_UPCOMING_MEETING = 'CLICK_ON_UPCOMING_MEETING',
  TOGGLE_ON_MEETING = 'TOGGLE_ON_MEETING',
  TOGGLE_OFF_MEETING = 'TOGGLE_OFF_MEETING',

  CLICK_ON_OUTLINE_TAB = 'CLICK_ON_OUTLINE_TAB',
  CLICK_ON_OUTLINE_ITEM_TO_EXPAND = 'CLICK_ON_OUTLINE_ITEM_TO_EXPAND',
  CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT = 'CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT',
  CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS = 'CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS',
  CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS = 'CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS',

  CHANGE_MINUTING_STYLES = 'CHANGE_MINUTING_STYLES',
  CHANGE_AGENDA_TYPE = 'CHANGE_AGENDA_TYPE',

  CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION = 'CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION',

  TOGGLE_REVISION_HISTORY = 'TOGGLE_REVISION_HISTORY',
  RESTORE_REVISION = 'RESTORE_REVISION',

  SEARCH_WITH_KEYWORD = 'SEARCH_WITH_KEYWORD',
  SEARCH_WITH_FILTER_BY_TYPE = 'SEARCH_WITH_FILTER_BY_TYPE',
  CLICK_ON_MEETING_IN_SEARCH_RESULTS = 'CLICK_ON_MEETING_IN_SEARCH_RESULTS',
  CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS = 'CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS',

  CLICK_ON_SPEAKERS_XRAY_BUTTON = 'CLICK_ON_SPEAKERS_XRAY_BUTTON',
  ASSIGN_SPEAKER_VIA_XRAY = 'ASSIGN_SPEAKER_VIA_XRAY',
  ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION = 'ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION',
  ENABLE_SPEAKER_LABELING_MODE = 'ENABLE_SPEAKER_LABELING_MODE',
  DISABLE_SPEAKER_LABELING_MODE = 'DISABLE_SPEAKER_LABELING_MODE',

  UPLOAD_RESOURCE = 'UPLOAD_RESOURCE',
  DELETE_RESOURCE = 'DELETE_RESOURCE',

  PAUSE_RECORDING = 'PAUSE_RECORDING',
  RESUME_RECORDING = 'RESUME_RECORDING',

  CLICK_TRANSCRIPT_TO_REPLAY_VIDEO = 'CLICK_TRANSCRIPT_TO_REPLAY_VIDEO',
  CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON = 'CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON',

  DOWNLOAD_VIDEO_RECORDING = 'DOWNLOAD_VIDEO_RECORDING',
  DOWNLOAD_AUDIO_RECORDING = 'DOWNLOAD_AUDIO_RECORDING',
  DOWNLOAD_TRANSCRIPT = 'DOWNLOAD_TRANSCRIPT',
  DOWNLOAD_MINUTES = 'DOWNLOAD_MINUTES',
  DOWNLOAD_OUTLINE = 'DOWNLOAD_OUTLINE',
  DOWNLOAD_ACTION_ITEMS = 'DOWNLOAD_ACTION_ITEMS',

  JOIN_MEETING_NOW = 'JOIN_MEETING_NOW',

  CREATE_MEETING = 'CREATE_MEETING',
  UPDATE_MEETING = 'UPDATE_MEETING',
  POSTPONE_MEETING = 'POSTPONE_MEETING',
  CANCEL_MEETING = 'CANCEL_MEETING',

  DELETE_MEETING_SOFT = 'DELETE_MEETING_SOFT',
  DELETE_MEETING_HARD = 'DELETE_MEETING_HARD',

  SHARE_MEETING_WITH_EXISTING_USER = 'SHARE_MEETING_WITH_EXISTING_USER',
  SHARE_MEETING_WITH_NEW_USER = 'SHARE_MEETING_WITH_NEW_USER',
  REPROCESS_OUTPUTS = 'REPROCESS_OUTPUTS',

  ADD_AGENDA_ITEM = 'ADD_AGENDA_ITEM',
  DELETE_AGENDA_ITEM = 'DELETE_AGENDA_ITEM',
  UPDATE_AGENDA_ITEM = 'UPDATE_AGENDA_ITEM',
  RESET_AGENDA_ITEMS = 'RESET_AGENDA_ITEMS',

  LOAD_TEMPLATE = 'LOAD_TEMPLATE',
  CREATE_BLANK_TEMPLATE = 'CREATE_BLANK_TEMPLATE',
  ADD_TEMPLATE_ITEM = 'ADD_TEMPLATE_ITEM',
  REMOVE_TEMPLATE_ITEM = 'REMOVE_TEMPLATE_ITEM',
  MOVE_TEMPLATE_ITEM = 'MOVE_TEMPLATE_ITEM',
  EDIT_TEMPLATE_ITEM = 'EDIT_TEMPLATE_ITEM',
  ATTACH_TEMPLATE_DOCUMENT = 'ATTACH_TEMPLATE_DOCUMENT',
  DETACH_TEMPLATE_DOCUMENT = 'DETACH_TEMPLATE_DOCUMENT',

  REGISTER_MEETING = 'REGISTER_MEETING',
  HANGUP_MEETING = 'HANGUP_MEETING',

  JOIN_MEETING = 'JOIN_MEETING',
  LEAVE_MEETING = 'LEAVE_MEETING',

  SEND_EMAIL = 'SEND_EMAIL',
  MARK_MEETING_AS_ERRORED = 'MARK_MEETING_AS_ERRORED',

  SEND_TO_TRANSCODING = 'SEND_TO_TRANSCODING',
  COMPLETE_TRANSCODING = 'COMPLETE_TRANSCODING',
  RECEIVE_TRANSCODER_AUDIO_READY = 'RECEIVE_TRANSCODER_AUDIO_READY',

  SEND_TO_TRANSCRIPTION = 'SEND_TO_TRANSCRIPTION',
  COMPLETE_TRANSCRIPTION = 'COMPLETE_TRANSCRIPTION',

  SEND_TO_OUTLINE_GENERATION = 'SEND_TO_OUTLINE_GENERATION',
  COMPLETE_OUTLINE_GENERATION = 'COMPLETE_OUTLINE_GENERATION',

  SEND_TO_MINUTES_GENERATION = 'SEND_TO_MINUTES_GENERATION',
  COMPLETE_MINUTES_GENERATION = 'COMPLETE_MINUTES_GENERATION',
  MINUTES_GENERATION_INPUT_TOKENS = 'MINUTES_GENERATION_INPUT_TOKENS',
  MINUTES_GENERATION_OUTPUT_TOKENS = 'MINUTES_GENERATION_OUTPUT_TOKENS',

  SEND_TO_AGENDA_EXTRACTION = 'SEND_TO_AGENDA_EXTRACTION',
  COMPLETE_AGENDA_EXTRACTION = 'COMPLETE_AGENDA_EXTRACTION',

  CREATE_USER = 'CREATE_USER',
  DELETE_USER = 'DELETE_USER',
  UPDATE_ORGANIZATION_SETTINGS = 'UPDATE_ORGANIZATION_SETTINGS',

  EDITOR_POLLING_FALLBACK = 'EDITOR_POLLING_FALLBACK',
}
export type MinutesStyling = {
  minutesVerboseness: MinutesVerboseness | null
  minutesAnonymization: MinutesAnonymization | null
  minutesPassive: MinutesPassive | null
  minutesNameFormat: MinutesNameFormat | null
  minutesDateFormat: MinutesDateFormat | null
  minutesTimeFormat: MinutesTimeFormat | null
  minutesLanguage: MinutesLanguage | null
  minutesAttribution: MinutesAttribution | null
}

export type UploadCallData = {
  name: string
  date: Date
  userIds: number[]
}

export type AddCallData = {
  link: string
  name: string
  userIds: number[]
  date: Date
  duration: number
}

export type ImportResourceData = {
  uploadKey: string
  targetResourceExtension: string
  fileName: string
  sourceDocumentFormat: 'PDF' | 'DOCX' | 'PPTX'
}

type AdminCallQueryResult = {
  list: AdminCallDetails[]
  total: number
}

export enum AgendaItemCategory {
  SUBSTANTIVE = 'SUBSTANTIVE',
  PROCEDURAL = 'PROCEDURAL',
}

export enum AgendaItemSubCategory {
  INTRODUCTION = 'INTRODUCTION',
  CHAIR_OF_THE_MEETING = 'CHAIR_OF_THE_MEETING',
  NOTICE_GIVEN_TO_DIRECTORS = 'NOTICE_GIVEN_TO_DIRECTORS',
  QUORUM = 'QUORUM',
  APOLOGIES_FOR_ABSENCE = 'APOLOGIES_FOR_ABSENCE',
  DECLARATIONS_OF_INTERESTS = 'DECLARATIONS_OF_INTERESTS',
  APPROVAL_OF_PREVIOUS_BOARD_MINUTES = 'APPROVAL_OF_PREVIOUS_BOARD_MINUTES',
  MATTERS_ARISING = 'MATTERS_ARISING',
  ANY_OTHER_BUSINESS = 'ANY_OTHER_BUSINESS',
  DATE_OF_NEXT_MEETING = 'DATE_OF_NEXT_MEETING',
  CLOSE_OF_MEETING = 'CLOSE_OF_MEETING',
  CHAIRS_UPDATE = 'CHAIRS_UPDATE',
  CHAIRS_INTRODUCTION = 'CHAIRS_INTRODUCTION',
  OTHER_PROCEDURAL = 'OTHER_PROCEDURAL',
  VERBAL_UPDATE = 'VERBAL_UPDATE',
  REPORT = 'REPORT',
  DECISIONS_APPROVALS = 'DECISIONS_APPROVALS',
  OTHER_SUBSTANTIVE = 'OTHER_SUBSTANTIVE',
}

export type AddAgendaNodeData = {
  parentId: number | null
  index: number
  title: string
  category: AgendaItemCategory
  subCategories: AgendaItemSubCategory[]
}

export type UpdateAgendaItemData = {
  id: number
  itemNumber: string | null
  title: string
  category: AgendaItemCategory
  subCategories: AgendaItemSubCategory[]
  presenters: string
}

export function hasUnlabeledSpeakers(call: Call): boolean {
  if (call.userSpeakerMapping === null) {
    return false
  }
  const sufficientlyLongTranscriptSpeakerSlots = (call.transcript.filter((slot) => slot.type === 'speaker') as TranscriptSpeakerSlot[]).filter(
    (slot) => slot.duration > MINIMUM_SEGMENT_DURATION
  )
  const speakerTagsInTranscript = sufficientlyLongTranscriptSpeakerSlots.map((transcriptSlot) => transcriptSlot.speaker.name!)
  const uniqueTags = [...new Set(speakerTagsInTranscript)]
  return uniqueTags.some((tag) => call.userSpeakerMapping![tag] === tag)
}

export function isDraftCall(call: { type: CallType; status: CallStatus }): boolean {
  return (call.type === CallType.VIDEO_UPLOAD || call.type === CallType.AUDIO_UPLOAD) && call.status === CallStatus.CREATED
}

export function hasEditAccess(call: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR'].includes(call.accessLevel)
}

export function canUserEdit(call: { accessLevel: string; status: CallStatus; isPostProcessingComplete: boolean }): boolean {
  if (!hasEditAccess(call)) {
    return false
  }

  if (call.status === CallStatus.COMPLETED && !call.isPostProcessingComplete) {
    return false
  }

  return true
}

export type AdminCreateCallData = {
  name: string
  link: string
  date: Date
  duration: number
  organizationId: number
  userIds: number[]
}

export type LoadTestData = {
  name: string
  botCount: number
  link: string
  date: Date
  duration: number
  organizationId: number
  userIds: number[]
}

type AdminCallListQueryFilters = {
  link?: string
  organizationIds?: number[]
  displaySetToJoinOnly?: boolean
  userIds?: number[]
  statuses?: CallStatus[]
  startDate?: string
  endDate?: string
}
type AdminCallListQuery = AdminCallListQueryFilters & ListParameterQueryOptional

export type AdminCompletedCallUser = {
  userId: number
  firstName: string
  lastName: string
  email: string
}

export type AdminCompletedCall = {
  callId: number
  organization: { id: number; name: string; client: { id: number; name: string } }
  users: AdminCompletedCallUser[]
  status: CallStatus
  type: CallType
  actualStartDate: string | null
  actualEndDate: string | null
  duration: number | null
  hardDeletedAt: string | null
  avgConfidenceScore: number | null
  avgRms: number | null
  avgHfRatio: number | null
}

type AdminCompletedCallQueryResult = {
  list: AdminCompletedCall[]
  total: number
}

type AdminCompletedCallListQueryFilters = {
  callId?: number
  organizationIds?: number[]
  userIds?: number[]
}
type AdminCompletedCallListQuery = AdminCompletedCallListQueryFilters & ListParameterQueryOptional

export type AgendaTranscriptMappingJobStatus = 'NOT_STARTED' | 'ONGOING' | 'SUCCEEDED' | 'FAILED'

export type AgendaTranscriptMappingListItem = {
  callId: number
  organizationId: number
  organizationName: string
  clientName: string
  callDate: string
  durationSeconds: number
  jobRunId: number
  jobStatus: AgendaTranscriptMappingJobStatus
  agendaItemCount: number
  discussedCount: number
  notDiscussedCount: number
  lowConfidenceSegmentCount: number
  unmappedDiscussionCount: number
  transcriptionCoveragePercent: number | null
}

type AgendaTranscriptMappingListQueryResult = { list: AgendaTranscriptMappingListItem[]; total: number }

export type AgendaTranscriptMappingListQuery = {
  callId?: number
  organizationIds?: number[]
  startDate?: string
  endDate?: string
  durationMinSeconds?: number
  durationMaxSeconds?: number
  jobStatus?: AgendaTranscriptMappingJobStatus
  hasLowConfidenceSegments?: boolean
  hasUnmappedDiscussions?: boolean
  discussedRatioMax?: number
  coverageMax?: number
  sortField?: 'callId' | 'callDate' | 'durationSeconds'
  sortDirection?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

type CallDetailsErrorUser = {
  id: number
  firstName: string
  lastName: string
  email: string
}
type CallDetailsError = {
  hasAccess: boolean
  sameOrganization: boolean
  callName: string
  users: CallDetailsErrorUser[]
}
type CallDetailsErrorData = {
  error: { message: CallDetailsError }
}

@Injectable({
  providedIn: 'root',
})
export class CallsService {
  mutation = injectMutation()
  httpClient = inject(HttpClient)
  query = injectQuery()
  infiniteQuery = injectInfiniteQuery()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  downloadsService = inject(DownloadsService)
  demoProfilesService = inject(DemoProfilesService)
  loggerService = inject(LoggerService)
  metricsService = inject(MetricsService)

  searchGuestOrganizationOptionsQueryParams = {
    queryKey: queryKeys.search.guestOrganizations,
    queryFn: () => this.httpClient.get<GuestOrganization[]>('/api/search/options/guest-organizations'),
  }

  searchAttendeeOptionsQueryParams = {
    queryKey: queryKeys.search.attendees,
    queryFn: () => this.httpClient.get<Attendee[]>('/api/search/options/attendees'),
  }

  searchSpeakerOptionsQueryParam = {
    queryKey: queryKeys.search.speakers,
    queryFn: () => this.httpClient.get<Speaker[]>('/api/search/options/speakers'),
  }

  constructor() {
    this.listenForActiveDemoProfileUpdates()
  }

  forceProcessBotOutputsMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`api/calls/${callId}/process-bot-outputs`, null),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.FORCE_PROCESS_BOT_OUTPUTS_SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.FORCE_PROCESS_BOT_OUTPUTS_FAILURE')
      },
    })
  }

  reprocessOutputsMutation() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<void>(`api/calls/${callId}/reprocess-outputs`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.toastService.show('CALLS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.REPROCESS.FAILURE')
      },
    })
  }

  sendRecoveryBotMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`api/calls/${callId}/send-recovery-bot`, null),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.SEND_RECOVERY_BOT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SEND_RECOVERY_BOT.FAILURE')
      },
    })
  }

  sendDirectBotMutation() {
    return this.mutation({
      mutationFn: ({ callId, startNow }: { callId: number; startNow: boolean }) =>
        this.httpClient.post<void>(`api/calls/${callId}/send-direct-bot`, { startNow }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.SEND_DIRECT_BOT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SEND_DIRECT_BOT.FAILURE')
      },
    })
  }

  resendBotMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`api/calls/${callId}/resend-bot`, null),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.toastService.show('CALLS.RESEND_BOT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.RESEND_BOT.FAILURE')
      },
    })
  }

  reprocessComponentMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        component,
        llmModel,
        stopAfterCompletion,
        minSpeakers,
        speakerCount,
        maxSpeakers,
      }: {
        callId: number
        component: PostProcessor
        llmModel: LlmModel | null
        stopAfterCompletion: boolean
        minSpeakers: number | null
        speakerCount: number | null
        maxSpeakers: number | null
      }) => {
        const params = new URLSearchParams({ component })
        if (llmModel !== null) {
          params.append('llmModel', llmModel)
        }
        if (stopAfterCompletion) {
          params.append('stopAfterCompletion', 'true')
        }
        if (minSpeakers !== null) {
          params.append('minSpeakers', String(minSpeakers))
        }
        if (speakerCount !== null) {
          params.append('speakerCount', String(speakerCount))
        }
        if (maxSpeakers !== null) {
          params.append('maxSpeakers', String(maxSpeakers))
        }
        return this.httpClient.post<void>(`api/calls/${callId}/reprocess?${params.toString()}`, null)
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.toastService.show('CALLS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.REPROCESS.FAILURE')
      },
    })
  }

  getUpcomingCalls() {
    return this.infiniteQuery({
      queryKey: queryKeys.calls.list.upcoming,
      queryFn: ({ pageParam }) => this.getUpcomingCallList(pageParam),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchInterval: 10000,
    })
  }

  getOngoingCalls() {
    return this.query({
      queryKey: queryKeys.calls.list.ongoing,
      queryFn: () => this.getOngoingCallList(),
      refetchInterval: 10000,
    }).result$
  }

  getPastCalls(callType: PastCallsType, showHiddenCalls: boolean) {
    return this.infiniteQuery({
      queryKey: queryKeys.calls.list.past.byType(callType),
      queryFn: ({ pageParam }) => this.getPastCallList(pageParam, callType, showHiddenCalls),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchInterval: 10000,
    })
  }
  getAdminCalls(params: AdminCallListQuery) {
    return this.query({
      queryKey: queryKeys.calls.base,
      queryFn: () => this.httpClient.get<AdminCallQueryResult>('/api/calls/support', { params }),
    }).result$
  }

  getSupportCompletedCalls(params: AdminCompletedCallListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.completed, params],
      queryFn: () => this.httpClient.get<AdminCompletedCallQueryResult>('/api/calls/support/completed-calls', { params }),
    }).result$
  }

  getSupportAgendaTranscriptMappingList(params: AgendaTranscriptMappingListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.agendaTranscriptMappings, params],
      queryFn: () => this.httpClient.get<AgendaTranscriptMappingListQueryResult>('/api/calls/support/agenda-transcript-mappings', { params }),
    }).result$
  }

  getAgendaTranscriptMappingForCall(callId: number, jobId: number) {
    return this.query({
      queryKey: queryKeys.calls.support.agendaTranscriptMapping(callId, jobId),
      queryFn: () =>
        this.httpClient.get<AgendaTranscriptMappingResponse | null>(`/api/calls/${callId}/support/agenda-transcript-mapping`, { params: { jobId } }),
    }).result$
  }

  getAdminCallsForUser(userId: number, params: AdminCallListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.byUserId(userId), params],
      queryFn: () => this.httpClient.get<AdminCallQueryResult>('/api/calls/support', { params: { ...params, userIds: [userId] } }),
    }).result$
  }

  getAdminCallsForOrganization(organizationId: number, params: AdminCallListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.byOrganizationId(organizationId), params],
      queryFn: () => this.httpClient.get<AdminCallQueryResult>('/api/calls/support', { params: { ...params, organizationIds: [organizationId] } }),
    }).result$
  }

  getCall(id: number) {
    return this.query<Call, CallDetailsErrorData>(this.getCallQueryParams(id)).result$
  }

  getAdminViewToCall(id: number): Observable<QueryObserverResult<AdminCallDetails>> {
    return this.query({
      queryKey: queryKeys.calls.support.byId(id),
      queryFn: () => this.httpClient.get<AdminCallDetails>(`/api/calls/${id}/support`),
      refetchInterval: 30000,
    }).result$
  }

  getDebugDataFiles(id: number): Observable<QueryObserverResult<DebugDataFile[]>> {
    return this.query({
      queryKey: queryKeys.calls.support.debugData(id),
      queryFn: () => this.httpClient.get<DebugDataFile[]>(`/api/calls/${id}/support/debug-data`),
    }).result$
  }

  getEditorDebug(id: number): Observable<QueryObserverResult<EditorAdminDebug | null>> {
    return this.query({
      queryKey: queryKeys.calls.support.editorDebug(id),
      queryFn: () => this.httpClient.get<EditorAdminDebug | null>(`/api/calls/${id}/editor/support-debug`),
    }).result$
  }

  getHallucinationAudit(callId: number, jobId: number): Observable<QueryObserverResult<HallucinationAuditData>> {
    return this.query({
      queryKey: queryKeys.calls.support.hallucinationAudit(callId, jobId),
      queryFn: () => this.httpClient.get<HallucinationAuditData>(`/api/calls/${callId}/support/hallucination-audit/${jobId}`),
    }).result$
  }

  private getCallQueryParams(id: number) {
    const urlParams = new URLSearchParams(window.location.search)
    const secret = urlParams.get('secret')
    const params = secret ? ({ secret } as any) : {}

    let failureCount = 0

    return {
      queryKey: queryKeys.calls.byId(id),
      queryFn: () => this.httpClient.get<Call>(`/api/calls/${id}`, { params }),
      retry: 0,
      refetchInterval: (query: Query<Call, CallDetailsErrorData>) => {
        if (query.state.data?.isIngestingBoardPack ?? false) {
          return 10000
        }

        if (query.state.data?.isPostProcessingComplete ?? false) {
          return false
        }

        if (query.state.error?.error?.message?.sameOrganization) {
          return false
        }

        const failed = query.state.fetchFailureReason !== null
        if (failed) {
          failureCount++
          if (failureCount === 3) {
            this.loggerService.error('The call details fetching failed three times. Stopping the refetch.')
            return false
          }
        }

        if (query.state.data?.isCheckingAgendaValidity ?? false) {
          return 1000
        }

        return 10000
      },
      staleTime: 10000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  }

  async prefetchCall(id: number) {
    await this.queryClient.prefetchQuery(this.getCallQueryParams(id))
  }

  getCallsOfUser(): Observable<QueryObserverResult<Call[]>> {
    const upcomingCalls$ = this.httpClient
      .get<CallListResponse>('/api/calls/upcoming', {
        params: { cursor: 0, limit: 1000 },
      })
      .pipe(map((response) => response.calls))
    const pastCalls$ = this.httpClient
      .get<CallListResponse>('/api/calls/past', {
        params: {
          cursor: 0,
          limit: 1000,
          callType: 'ALL',
          showHiddenCalls: true,
        },
      })
      .pipe(map((response) => response.calls))
    return forkJoin([upcomingCalls$, pastCalls$]).pipe(
      map(([upcomingCalls, pastCalls]) => [...upcomingCalls, ...pastCalls]),
      mergeMap(
        (calls) =>
          this.query<Call[]>({
            queryKey: queryKeys.calls.base,
            queryFn: () => of(calls),
          }).result$
      )
    )
  }

  uploadCall() {
    return this.mutation({
      mutationFn: (data: UploadCallData) => this.httpClient.post<Call>(`/api/calls/upload`, data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.past.base })
      },
    })
  }

  joinLiveCall() {
    return this.mutation({
      mutationFn: (data: AddCallData) => this.httpClient.post<Call>(`/api/calls`, data),
      onError: (error: any) => {
        const errorMessages: { [key: string]: string } = {
          CALL_WITH_LINK_ALREADY_EXISTS: 'CALLS.ADD_CALL.CALL_WITH_LINK_ALREADY_EXISTS',
          CALL_LINK_INVALID: 'CALLS.ADD_CALL.CALL_LINK_INVALID',
          TEAMS_SHORT_LINK_NOT_SUPPORTED: 'CALLS.ADD_CALL.TEAMS_SHORT_LINK_NOT_SUPPORTED',
        }
        const errorMessage = errorMessages[error?.error?.message] || 'CALLS.ADD_CALL.FAILURE'
        this.toastService.show(errorMessage)
      },
    })
  }

  importResource() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: ImportResourceData }) =>
        this.httpClient.post<CallResource>(`/api/calls/${callId}/import-resource`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.IMPORT_SUCCESS')
      },
    })
  }

  importAgenda() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: ImportResourceData }) =>
        this.httpClient.post<CallResource>(`/api/calls/${callId}/import-agenda`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.IMPORT_SUCCESS')
      },
    })
  }

  removeResource() {
    return this.mutation({
      mutationFn: ({ callId, resourceType, index, extension }: { callId: number; resourceType: string; index: number; extension: string }) =>
        this.httpClient.delete<void>(`/api/calls/${callId}/resources`, {
          body: { resourceType, index, extension },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  removeAllResourcesOfType() {
    return this.mutation({
      mutationFn: ({ callId, resourceType }: { callId: number; resourceType: string }) =>
        this.httpClient.delete<void>(`/api/calls/${callId}/resources/by-type`, {
          body: { resourceType },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  removeResourceSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, resourceType, index, extension }: { callId: number; resourceType: string; index: number; extension: string }) =>
        this.httpClient.delete<void>(`/api/calls/${callId}/resources/support`, {
          body: { resourceType, index, extension },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  importRecording() {
    return this.mutation({
      mutationFn: ({ callId, uploadKey, fileExtension, fileName }: { callId: number; uploadKey: string; fileExtension: string; fileName: string }) =>
        this.httpClient.post<Call>(`/api/calls/${callId}/import-recording`, { uploadKey, fileExtension, fileName }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.UPLOAD_RECORDING.UPLOAD_FAILED')
      },
    })
  }

  completeSetup() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<Call>(`/api/calls/${callId}/complete-setup-for-uploaded-call`, {}),
      onSuccess: (_, params) => {
        this.queryClient.resetQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.COMPLETE_SETUP.FAILED')
      },
    })
  }

  patchMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: Partial<Call> }) => this.httpClient.patch<void>(`/api/calls/${callId}`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
    })
  }

  patchSupportMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        data,
      }: {
        callId: number
        data: Omit<
          Partial<Call>,
          | 'useNewEditor'
          | 'useNativeBot'
          | 'minutingAgents'
          | 'overrideOrganizationLevelMinutingAgents'
          | 'useVerificationDiscovery'
          | 'anonymiseMemberSpeakers'
        > & {
          useNewEditor?: boolean | null
          useNativeBot?: boolean | null
          minutingAgents?: MinutingAgent[]
          overrideOrganizationLevelMinutingAgents?: boolean
          useVerificationDiscovery?: boolean | null
          useAgendaTranscriptMapping?: AgendaTranscriptMappingMode | null
          anonymiseMemberSpeakers?: boolean | null
        }
      }) => this.httpClient.patch<void>(`/api/calls/${callId}/support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
    })
  }

  updateAgendaTypeMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { agendaType: AgendaType } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/agenda-type`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateAgendaTypeSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { agendaType: AgendaType } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/agenda-type/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  updateMediumMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { medium: CallMedium } }) => this.httpClient.post<void>(`/api/calls/${callId}/medium`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateMediumSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { medium: CallMedium } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/medium/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  updateCallProfileMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { callProfileId: number | null } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/call-profile`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateCallProfileSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { callProfileId: number | null } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/call-profile/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  assignTemplateSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, templateId }: { callId: number; templateId: number }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/assign-template`, { templateId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
        this.toastService.show('TEMPLATES.IMPORTED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.IMPORT_FAILED')
      },
    })
  }

  splitSpeakersMutation() {
    return this.mutation({
      mutationFn: ({ callId, speakerTag }: { callId: number; speakerTag: string }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/split-speakers`, { speakerTag }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.toastService.show('CALLS.SPEAKERS.SPLIT_SUCCEEDED')
      },
      onError: () => {
        this.toastService.show('CALLS.SPEAKERS.SPLIT_FAILED')
      },
    })
  }

  patchSpeakerNameMutation() {
    return this.mutation({
      mutationFn: ({ callId, speakerTag, speakerName }: { callId: number; speakerTag: string; speakerName: string }) =>
        this.httpClient.patch<void>(`/api/calls/${callId}/speaker-mapping`, { speakerTag, speakerName }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  pauseRecording(callId: number) {
    return this.httpClient.post<void>(`/api/calls/${callId}/pause-recording`, {}).subscribe({
      next: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_PAUSE.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_PAUSE.FAILURE')
      },
    })
  }

  resumeRecording(callId: number) {
    return this.httpClient.post<void>(`/api/calls/${callId}/resume-recording`, {}).subscribe({
      next: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_RESUME.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_RESUME.FAILURE')
      },
    })
  }

  hangUpCall(callId: number) {
    this.httpClient.post<void>(`/api/calls/${callId}/hang-up`, {}).subscribe({
      next: () => {
        this.toastService.show('CALLS.HANG_UP.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.HANG_UP.FAILURE')
      },
    })
  }

  hangUpAsAdminMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/hang-up-as-support`, {}),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.HANG_UP.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.HANG_UP.FAILURE')
      },
    })
  }

  solveCaptchaMutation() {
    return this.mutation({
      mutationFn: ({ callId, code }: { callId: number; code: string }) => this.httpClient.post<void>(`/api/calls/${callId}/support/solve-captcha`, { code }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.debugData(callId) })
        this.toastService.show('CALLS.ADMIN.SOLVE_CAPTCHA.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SOLVE_CAPTCHA.FAILURE')
      },
    })
  }

  hangUpCallAnonymously(callId: number, accessKey: string): Observable<boolean> {
    return this.httpClient.post<void>(`/api/bot/${callId}/hang-up`, {}, { headers: { 'X-Call-Access-Key': accessKey } }).pipe(
      map(() => {
        this.toastService.show('CALLS.HANG_UP.SUCCESS')
        return true
      }),
      catchError(() => {
        this.toastService.show('CALLS.HANG_UP.FAILURE')
        return of(false)
      })
    )
  }

  previewExtendCallAnonymously(callId: number, accessKey: string): Observable<ExtendPreview> {
    return this.httpClient
      .get<ExtendPreview>(`/api/bot/${callId}/extend`, { headers: { 'X-Call-Access-Key': accessKey } })
      .pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
  }

  extendCallAnonymously(callId: number, accessKey: string): Observable<ExtendResult> {
    return this.httpClient
      .post<ExtendResult>(`/api/bot/${callId}/extend`, {}, { headers: { 'X-Call-Access-Key': accessKey } })
      .pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
  }

  switchToNextSubCallMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/save-split-offset`, {}),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
        this.toastService.show('CALLS.ONGOING_CALLS.PROCEED_TO_NEXT_SUB_CALL.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.PROCEED_TO_NEXT_SUB_CALL.FAILURE')
      },
    })
  }

  setEndDateMutation() {
    return this.mutation({
      mutationFn: ({ callId, endDate }: { callId: number; endDate: Date }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/set-end-date`, { endDate: endDate.toISOString() }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  extendCallMutation() {
    return this.mutation({
      mutationFn: (callId: number) =>
        firstValueFrom(
          this.httpClient.post<ExtendResult>(`/api/calls/${callId}/extend`, {}).pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
        ),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  setRetentionPeriodStartDateMutation() {
    return this.mutation({
      mutationFn: ({ callId, retentionPeriodStartDate }: { callId: number; retentionPeriodStartDate: Date }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/set-retention-period-start-date`, {
          retentionPeriodStartDate: retentionPeriodStartDate.toISOString(),
        }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADJUST_RETENTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADJUST_RETENTION.FAILURE')
      },
    })
  }

  requestCallAccess(callId: number) {
    return this.httpClient.post<void>(`/api/calls/${callId}/request-access`, {}).subscribe({
      next: () => {
        this.toastService.show('CALLS.ACCESS_REQUEST.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.ACCESS_REQUEST.FAILURE')
      },
    })
  }

  setIsSetToJoinMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/set-to-join`, {}),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  cancelSessionCloudRunExecutionMutation() {
    return this.mutation({
      mutationFn: ({ callId, sessionIndex }: { callId: number; sessionIndex: number }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/sessions/${sessionIndex}/cancel-cloud-run-execution`, {}),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.CANCEL_CLOUD_RUN_EXECUTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.CANCEL_CLOUD_RUN_EXECUTION.FAILURE')
      },
    })
  }

  setSoftDeletionMutation() {
    return this.mutation({
      mutationFn: ({ callId, markedToBeDeleted }: { callId: number; markedToBeDeleted: boolean }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/set-soft-deletion`, { markedToBeDeleted }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  setCallStatusMutation() {
    return this.mutation({
      mutationFn: ({ callId, status }: { callId: number; status: CallStatus }) => this.httpClient.post<void>(`/api/calls/${callId}/set-status`, { status }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  setCallSessionStatusMutation() {
    return this.mutation({
      mutationFn: ({ callId, sessionIndex, status }: { callId: number; sessionIndex: number; status: CallSessionStatus }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/sessions/${sessionIndex}/set-status`, { status }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  approveAccessRequest() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/approve-access`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.accessRequests.base })
      },
    })
  }

  revokeAccessRequest() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/revoke-access`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.accessRequests.base })
      },
    })
  }

  getCallAccessRequest(callId: number) {
    return this.query({
      queryKey: queryKeys.calls.accessRequests.byId(callId),
      queryFn: () =>
        this.httpClient.get<{
          callId: number
          accessProvided: boolean
        }>(`/api/calls/${callId}/check-access`),
    }).result$
  }

  getCallAccessRequests() {
    return this.query({
      queryKey: queryKeys.calls.accessRequests.base,
      queryFn: () => this.httpClient.get<Call[]>('/api/calls/access-requests'),
    }).result$
  }

  exportTranscript(call: Call, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(call, 'transcript', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export/${format}?outlineSelected=false&transcriptSelected=true`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.TRANSCRIPT_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportMinutes(call: Call, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(call, 'minutes', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export-editor-to-${format}`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.MINUTES_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportOutline(call: Call, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(call, 'outline', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export/${format}?outlineSelected=true&transcriptSelected=false`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.OUTLINE_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportActionItems(call: Call, format: 'csv'): Observable<void> {
    const fileName = generateFileName(call, 'action-items', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export/action-items`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.ACTION_ITEMS_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  getSearchGuestOrganizationOptions() {
    return this.query(this.searchGuestOrganizationOptionsQueryParams).result$
  }

  async prefetchSearchFilterOptions() {
    await this.queryClient.prefetchQuery(this.searchGuestOrganizationOptionsQueryParams)
    await this.queryClient.prefetchQuery(this.searchAttendeeOptionsQueryParams)
    // await this.queryClient.prefetchQuery(this.searchSpeakerOptionsQueryParam)
  }

  getSearchAttendeeOptions() {
    return this.query(this.searchAttendeeOptionsQueryParams).result$
  }

  getSearchSpeakerOptions() {
    return this.query(this.searchSpeakerOptionsQueryParam).result$
  }

  private getUpcomingCallList(cursor: number): Observable<CallListResponse> {
    const params = this.addDemoProfileId({
      cursor,
    })
    return this.httpClient.get<CallListResponse>('/api/calls/upcoming', { params })
  }

  private getOngoingCallList(): Observable<CallWithLatestSession[]> {
    return this.httpClient.get<CallWithLatestSession[]>('/api/calls/ongoing')
  }

  private getPastCallList(cursor: number, callType: PastCallsType, showHiddenCalls: boolean): Observable<CallListResponse> {
    const params = this.addDemoProfileId({
      cursor,
      callType,
      showHiddenCalls,
    })
    return this.httpClient.get<CallListResponse>('/api/calls/past', { params })
  }

  search(filters: CallFilters): Observable<QueryObserverResult<SearchResponse>> {
    return this.query<SearchResponse>({
      queryKey: queryKeys.search.results.byFilters(filters),
      queryFn: () => this.searchCalls(filters),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }).result$
  }

  private searchCalls(filters: CallFilters): Observable<SearchResponse> {
    const data = this.addDemoProfileId({
      organizationIds: filters.guestOrganizationId !== null ? [filters.guestOrganizationId] : [],
      attendees: filters.attendeeId !== null ? [filters.attendeeId] : [],
      startDate: filters.startDate,
      endDate: filters.endDate,
      keyword: filters.query,
      speakers: filters.speakerIds,
      showHiddenCalls: false, // TODO
      showOnlyPastMeetings: filters.showOnlyPastMeetings,
    })
    return this.httpClient.post<SearchResponse>('/api/search/calls', data)
  }

  updateCallAccessLevel(callId: number) {
    return this.mutation({
      mutationFn: ({ sharedUserId, level }: { sharedUserId: number; level: CallAccessLevel }) =>
        this.httpClient.post<Call>(`/api/calls/${callId}/update-share-level-for-shared-user`, { sharedUserId, level }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  shareToUser(callId: number) {
    return this.mutation({
      mutationFn: ({ sharedUserId, level, notifyByEmail }: { sharedUserId: number; level: CallAccessLevel; notifyByEmail: boolean }) =>
        this.httpClient.post<CallShare>(`/api/calls/${callId}/share-to-user`, { sharedUserId, level, notifyByEmail }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  removeShareMutation(callId: number) {
    return this.mutation({
      mutationFn: (sharedUserId: number) => this.httpClient.delete<void>(`/api/calls/${callId}/shares/${sharedUserId}`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  publicLinkAccessLevelMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        availability,
        level,
      }: {
        callId: number
        availability: CallPublicLinkAvailability | undefined
        level: CallAccessLevel | undefined
      }) => this.httpClient.patch<void>(`/api/calls/${callId}`, { publicLinkAccessLevel: level, publicLinkAvailability: availability }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  assignSentencesToSpeakerMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        startSentenceId,
        endSentenceId,
        speakerTag,
      }: {
        callId: number
        startSentenceId: string
        endSentenceId: string
        speakerTag: string
      }) => this.httpClient.post<void>(`/api/calls/${callId}/assign-sentences-to-speaker`, { startSentenceId, endSentenceId, speakerTag }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }, {})
      },
    })
  }

  addSpeakerMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        speakerName,
        startSentenceId,
        endSentenceId,
      }: {
        callId: number
        speakerName: string
        startSentenceId: string
        endSentenceId: string
      }) => this.httpClient.post<void>(`/api/calls/${callId}/add-speaker`, { speakerName, startSentenceId, endSentenceId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }, {})
      },
    })
  }

  resetSpeakersMutation() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<void>(`/api/calls/${callId}/reset-speakers`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }, {})
      },
    })
  }

  toggleExcludeCallMutation() {
    return this.mutation({
      mutationFn: ({ callId, isSetToJoin }: { callId: number; isSetToJoin: boolean }) => this.httpClient.put(`/api/calls/${callId}/user-data`, { isSetToJoin }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
      },
    })
  }

  deleteCallMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.delete<void>(`/api/calls/${callId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
      },
    })
  }

  toggleNotificationSetting() {
    return this.mutation({
      mutationFn: ({ callId, notifyWhenPostProcessingCompletes }: { callId: number; notifyWhenPostProcessingCompletes: boolean }) =>
        this.httpClient.put(`/api/calls/${callId}/user-data`, { notifyWhenPostProcessingCompletes }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
      },
    })
  }

  forceDisableJoiningMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/force-disable-joining`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.upcoming })
      },
    })
  }

  toggleCallUserJoinMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId, isSetToJoin }: { callId: number; userId: number; isSetToJoin: boolean }) =>
        this.httpClient.put<void>(`/api/calls/${callId}/user-data/toggle-join`, { userId, isSetToJoin }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  updateCallUserSupportMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        userId,
        hasWeekendDeletionReminderEmailBeenSent,
        isPrimaryCalendar,
      }: {
        callId: number
        userId: number
        hasWeekendDeletionReminderEmailBeenSent?: boolean
        isPrimaryCalendar?: boolean
      }) => this.httpClient.put<void>(`/api/calls/${callId}/user-data/support`, { userId, hasWeekendDeletionReminderEmailBeenSent, isPrimaryCalendar }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  deleteCallUserMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.delete<void>(`/api/calls/${callId}/call-users/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  addCallUserMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.post<void>(`/api/calls/${callId}/call-users`, { userId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  private addDemoProfileId(params: any) {
    const demoProfileId = this.demoProfilesService.activeDemoProfileId$.value
    if (demoProfileId === null) {
      return params
    }
    return { ...params, demoProfileId }
  }

  private listenForActiveDemoProfileUpdates() {
    this.demoProfilesService.activeDemoProfileId$.subscribe(() => {
      this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
    })
  }

  createAgendaItemMutation(callId: number) {
    return this.mutation({
      mutationFn: (data: AddAgendaNodeData) => this.httpClient.post<RawAgendaItem>(`/api/calls/${callId}/agenda-items`, data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  updateAgendaItemMutation(callId: number) {
    return this.mutation({
      mutationFn: (data: UpdateAgendaItemData) => this.httpClient.put<RawAgendaItem>(`/api/calls/${callId}/agenda-items/${data.id}`, data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  deleteAgendaItemMutation(callId: number) {
    return this.mutation({
      mutationFn: (agendaItemId: number) => this.httpClient.delete<void>(`/api/calls/${callId}/agenda-items/${agendaItemId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  deleteAgendaItemButKeepChildrenMutation(callId: number) {
    return this.mutation({
      mutationFn: (agendaItemId: number) => this.httpClient.delete<void>(`/api/calls/${callId}/agenda-items/${agendaItemId}/keep-children`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  moveAgendaItemUpMutation(callId: number) {
    return this.mutation({
      mutationFn: (data: MoveAgendaNodeData) =>
        this.httpClient.post<void>(`/api/calls/${callId}/agenda-items/${data.nodeId}/move-up`, {
          index: data.index,
        }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  moveAgendaItemDownMutation(callId: number) {
    return this.mutation({
      mutationFn: (data: MoveAgendaNodeData) =>
        this.httpClient.post<void>(`/api/calls/${callId}/agenda-items/${data.nodeId}/move-down`, {
          index: data.index,
        }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  indentAgendaItemMutation(callId: number) {
    return this.mutation({
      mutationFn: (nodeId: number) => this.httpClient.post<void>(`/api/calls/${callId}/agenda-items/${nodeId}/indent`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  outdentAgendaItemMutation(callId: number) {
    return this.mutation({
      mutationFn: (nodeId: number) => this.httpClient.post<void>(`/api/calls/${callId}/agenda-items/${nodeId}/outdent`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  resetAgendaItemsMutation(callId: number) {
    return this.mutation({
      mutationFn: () => this.httpClient.post<Agenda>(`/api/calls/${callId}/reset-agenda-items`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  addAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; data: Omit<AdminAgendaAttendee, 'id'> }) =>
        this.httpClient.post<AdminAgendaAttendee>(`/api/calls/${params.callId}/attendees`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; data: AdminAgendaAttendee }) =>
        this.httpClient.put<AdminAgendaAttendee>(`/api/calls/${params.callId}/attendees/${params.data.id}`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  deleteAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; attendeeId: number }) =>
        this.httpClient.delete<void>(`/api/calls/${params.callId}/attendees/${params.attendeeId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateMetadataMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; data: Partial<AdminAgendaMetadata> }) =>
        this.httpClient.patch<AdminAgendaMetadata>(`/api/calls/${params.callId}/metadata`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  joinNowMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/join-now`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.upcoming })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
      },
    })
  }

  createCallAsAdminMutation() {
    return this.mutation({
      mutationFn: (data: AdminCreateCallData) => this.httpClient.post<Call>(`/api/calls/support`, data),
    })
  }

  createLoadTestMutation() {
    return this.mutation({
      mutationFn: (data: LoadTestData) =>
        this.httpClient.post<{ success: boolean; callCount: number; calls: { id: number; name: string }[] }>(`/api/calls/load-test`, data),
    })
  }

  approveMissingAgendaMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<Call>(`/api/calls/${callId}/approve-missing-agenda`, {}),
      onSuccess: (_, callId: number) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  getCallTimeline(callId: number) {
    return this.query<CallTimelineResponse>({
      queryKey: [...queryKeys.calls.byId(callId), 'timeline'],
      queryFn: () => this.httpClient.get<CallTimelineResponse>(`/api/calls/${callId}/timeline`),
    }).result$
  }

  ingestBoardPackMutation() {
    return this.mutation({
      mutationFn: (data: { callId: number; uploadKey: string; fileName: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/calls/${data.callId}/ingest-board-pack`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.toastService.show('CALLS.ADMIN.BOARD_PACK.INGEST_SUCCESS')
      },
    })
  }

  extractParticipantsMutation() {
    return this.mutation({
      mutationFn: (data: { callId: number; uploadKey: string; fileName: string; fileExtension: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/calls/${data.callId}/extract-participants`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
          fileExtension: data.fileExtension,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.EXTRACT_PARTICIPANTS.FAILED')
      },
    })
  }

  replaceAttendeesSupportMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; attendees: Omit<AdminAgendaAttendee, 'id'>[] }) =>
        this.httpClient.put<AdminAgendaAttendee[]>(`/api/calls/${params.callId}/attendees-support`, { attendees: params.attendees }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  extractParticipantsSupportMutation() {
    return this.mutation({
      mutationFn: (data: { callId: number; uploadKey: string; fileName: string; fileExtension: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/calls/${data.callId}/extract-participants-support`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
          fileExtension: data.fileExtension,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.EXTRACT_PARTICIPANTS.FAILED')
      },
    })
  }
}
